import React, { useEffect, useState } from 'react';
import AppShell from '@/components/AppShell';
import client from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import DriverDashboard from '@/pages/DriverDashboard';
import { Card } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/StatusBadge';
import { FmcsaWidget } from '@/components/FmcsaWidget';
import { UsageMeter } from '@/components/UsageMeter';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
  PieChart, Pie, Legend, CartesianGrid,
} from 'recharts';
import {
  Truck, Box, Users2, FileWarning, ScrollText, Activity, ClipboardCheck,
  AlertOctagon, Car, ShieldAlert
} from 'lucide-react';

const GOLD = '#F5B301', BLUE = '#2563EB', GREEN = '#16A34A', SILVER = '#94A3B8', RED = '#DC2626', INFO = '#0EA5E9';

function KpiIcon({ icon: Icon, color }) {
  return <div className="flex h-12 w-12 items-center justify-center rounded-xl" style={{ background: `${color}1a` }}><Icon className="h-6 w-6" style={{ color }} /></div>;
}

export default function Dashboard() {
  const { user } = useAuth();
  if (user?.role === 'driver') return <DriverDashboard />;
  return <StaffDashboard />;
}

function StaffDashboard() {
  const navigate = useNavigate();
  const [hl, setHl] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [trends, setTrends] = useState(null);
  const [activity, setActivity] = useState([]);
  const [todos, setTodos] = useState([]);
  const [months, setMonths] = useState(6);

  useEffect(() => {
    client.get('/dashboard/highlights').then((r) => setHl(r.data)).catch(() => {});
    client.get('/dashboard/alerts-by-age').then((r) => setAlerts(r.data)).catch(() => {});
    client.get('/activity?limit=8').then((r) => setActivity(r.data)).catch(() => {});
    client.get('/todos').then((r) => setTodos(r.data)).catch(() => {});
  }, []);
  useEffect(() => { client.get(`/dashboard/trends?months=${months}`).then((r) => setTrends(r.data)).catch(() => {}); }, [months]);

  const donutData = alerts ? [
    { name: 'Missing / Past Due', value: alerts.buckets.missing, color: RED },
    { name: '< 5 Days', value: alerts.buckets.lt5, color: GOLD },
    { name: '5-10 Days', value: alerts.buckets.d5_10, color: INFO },
    { name: '30+ Days', value: alerts.buckets.d30plus, color: BLUE },
  ] : [];
  const donutTotal = donutData.reduce((a, b) => a + b.value, 0);

  return (
    <AppShell title="Dashboard">
      <Tabs defaultValue="highlights">
        <TabsList className="mb-6">
          <TabsTrigger value="highlights" data-testid="dashboard-highlights-tab">Highlights</TabsTrigger>
          <TabsTrigger value="trends" data-testid="dashboard-trends-tab">Trends</TabsTrigger>
        </TabsList>

        <TabsContent value="highlights" className="space-y-6">
          {!hl ? <div className="grid gap-4 md:grid-cols-3">{[1,2,3].map(i=><Skeleton key={i} className="h-32" />)}</div> : (
          <>
            {/* Row: FMCSA + fleet counts */}
            <div className="grid gap-4 lg:grid-cols-3">
              <FmcsaWidget />
              <Card className="flex items-center justify-around p-5 lg:col-span-2">
                {[{ icon: Users2, label: 'Drivers', v: hl.fleet.drivers, c: BLUE }, { icon: Truck, label: 'Power Units', v: hl.fleet.power_units, c: GOLD }, { icon: Box, label: 'Trailers', v: hl.fleet.trailers, c: SILVER }].map((x) => (
                  <div key={x.label} className="flex items-center gap-3">
                    <KpiIcon icon={x.icon} color={x.c} />
                    <div><div className="text-xs uppercase tracking-wide text-muted-foreground">{x.label}</div><div className="font-display text-3xl font-bold tabular-nums" data-testid={`kpi-${x.label.toLowerCase().replace(/ /g,'-')}`}>{x.v}</div></div>
                  </div>
                ))}
              </Card>
            </div>

            {/* Row: workspace usage vs plan limits */}
            <UsageMeter variant="compact" />

            {/* Row: updates + ISS + inspections */}
            <div className="grid gap-4 lg:grid-cols-4">
              <Card className="p-5">
                <div className="mb-3 font-display font-semibold">Monthly Updates</div>
                {[['Inspections', hl.monthly.inspections, RED], ['Violations', hl.monthly.violations, RED], ['Accidents', hl.monthly.accidents, GREEN]].map(([l, v, c]) => (
                  <div key={l} className="flex items-center justify-between border-b py-2 last:border-0"><span className="flex items-center gap-2 text-sm"><span className="h-2 w-2 rounded-full" style={{ background: c }} />{l}</span><span className="font-semibold tabular-nums">{v}</span></div>
                ))}
              </Card>
              <Card className="flex flex-col justify-between bg-[hsl(var(--primary))] p-5 text-white">
                <div className="font-display text-lg font-semibold">ISS Score</div>
                <div className="font-display text-6xl font-bold tabular-nums" data-testid="dashboard-iss-score-value">{hl.fmcsa.iss_score}</div>
                <div className="text-xs text-white/70">Inspection Selection System</div>
              </Card>
              <Card className="flex flex-col justify-between p-5" style={{ background: '#B91C1C', color: 'white' }}>
                <div className="font-display text-base font-semibold">Total Inspections</div>
                <div className="font-display text-5xl font-bold tabular-nums">{hl.fmcsa.total_inspections}</div>
                <div className="flex gap-4 text-xs"><div><div className="text-xl font-bold">{hl.fmcsa.inspections_with_violations}</div>w/ Violations</div><div><div className="text-xl font-bold">{hl.fmcsa.inspections_without_violations}</div>Without</div></div>
              </Card>
              <Card className="p-5">
                <div className="mb-3 font-display font-semibold">Driver Compliance</div>
                {[['Compliant', hl.compliance.compliant, 'compliant'], ['Warning', hl.compliance.warning, 'warning'], ['Non-Compliant', hl.compliance.non_compliant, 'non_compliant'], ['Pending Docs', hl.pending_docs, 'pending']].map(([l, v, s]) => (
                  <div key={l} className="flex items-center justify-between py-1.5"><StatusBadge status={s} label={l} /><span className="font-semibold tabular-nums">{v}</span></div>
                ))}
              </Card>
            </div>

            {/* Row: alerts donut + BASIC + activity */}
            <div className="grid gap-4 lg:grid-cols-3">
              <Card className="p-5">
                <div className="mb-2 font-display font-semibold">Outstanding Alerts by Age</div>
                <div className="relative">
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart data-testid="alerts-age-donut-chart">
                      <Pie data={donutData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={80} paddingAngle={2}>
                        {donutData.map((e, i) => <Cell key={i} fill={e.color} />)}
                      </Pie>
                      <Tooltip />
                      <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="pointer-events-none absolute inset-0 top-[-40px] flex flex-col items-center justify-center"><div className="font-display text-3xl font-bold">{donutTotal}</div><div className="text-xs text-muted-foreground">Total</div></div>
                </div>
              </Card>
              <Card className="p-5 lg:col-span-2">
                <div className="mb-2 font-display font-semibold">BASIC Categories (Violations)</div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={trends?.basic_categories || []} layout="vertical" margin={{ left: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="category" width={130} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" radius={[0, 6, 6, 0]} fill={BLUE} barSize={16} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <Card className="p-5">
                <div className="mb-3 flex items-center gap-2 font-display font-semibold"><Activity className="h-4 w-4" />Recent Activity</div>
                <div className="space-y-3">
                  {activity.map((a, i) => {
                    const IconMap = { inspection: ClipboardCheck, violation: AlertOctagon, accident: Car, driver: Users2, document: ScrollText, fleet: Truck, service_request: ShieldAlert };
                    const Ic = IconMap[a.activity_type] || Activity;
                    return <div key={i} className="flex items-start gap-3 text-sm"><Ic className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" /><div><div>{a.message}</div><div className="text-xs text-muted-foreground">{new Date(a.created_at).toLocaleString()}</div></div></div>;
                  })}
                  {!activity.length && <p className="text-sm text-muted-foreground">No recent activity.</p>}
                </div>
              </Card>
              <Card className="p-5">
                <div className="mb-3 flex items-center justify-between font-display font-semibold"><span className="flex items-center gap-2"><FileWarning className="h-4 w-4" />Company To-Dos</span><button className="text-xs font-medium text-[hsl(var(--primary))]" onClick={() => navigate('/todos')}>View All</button></div>
                <div className="space-y-2">
                  {todos.slice(0, 6).map((t) => (
                    <div key={t.id} className="flex items-center justify-between rounded-lg border p-2.5 text-sm">
                      <span>{t.title}</span>
                      <StatusBadge status={t.days_to_due != null && t.days_to_due < 0 ? 'expired' : t.days_to_due <= 5 ? 'warning' : 'valid'} label={t.days_to_due != null ? (t.days_to_due < 0 ? 'Overdue' : `${t.days_to_due}d`) : t.category} />
                    </div>
                  ))}
                  {!todos.length && <p className="text-sm text-muted-foreground">No open to-dos.</p>}
                </div>
              </Card>
            </div>
          </>) }
        </TabsContent>

        <TabsContent value="trends" className="space-y-6">
          <Card className="p-5">
            <div className="mb-4 flex items-center justify-between">
              <div className="font-display font-semibold">BASIC Safety Categories Trend</div>
              <div className="flex gap-1 rounded-lg border p-1">
                {[1,2,3,4,5,6].map((m) => <button key={m} onClick={() => setMonths(m)} className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${months===m?'bg-[hsl(var(--primary))] text-white':'text-muted-foreground hover:bg-muted'}`} data-testid={`trends-month-${m}`}>{m}m</button>)}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={trends?.basic_categories || []}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="category" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {(trends?.basic_categories || []).map((e, i) => <Cell key={i} fill={[BLUE, GOLD, GREEN, INFO, RED, SILVER][i % 6]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}
